import { frameId, type ManifestObjectAppearance, type TileRef } from "daedalus-core";
import type { ThingFrame } from "./thing-playback.ts";

/**
 * Presence playback on the client (ADR-058 amendment, P1.3-T4). A prop driven by presence shows its inside
 * state while the viewer's own character stands in its area and its initial state otherwise, on that
 * viewer's screen alone; the server holds nothing for it. The game tells this module each frame whether the
 * character is inside, and it answers with the frame to draw. The rules it enforces:
 *
 * - A strip starts on its second frame. Its first is the drawing of the state it leaves, which the prop
 *   already shows; the cabin's closing strips start on a frame the artist left blank.
 * - A change of mind retraces the strip on screen from the frame shown, at its durations, and rests on the
 *   state it left on reaching its first frame; turning back again resumes forward. It never switches to the
 *   other strip mid-way, so no drawing shows out of order.
 * - First sight animates nothing: the first update after `add` or `reset` rests on the state it finds.
 * - A change whose strip the author left out, or whose strip has one frame, cuts.
 *
 * Frames are computed once per appearance and a slot's record is made when it is added, so updating and
 * asking what to draw allocate nothing.
 */

/**
 * One strip ready to play: the states it moves between, its frames, the time each frame ends in milliseconds
 * from the strip's start, when its second frame starts, and its length.
 */
interface PreparedStrip {
  readonly from: number;
  readonly to: number;
  readonly frames: readonly ThingFrame[];
  readonly ends: readonly number[];
  readonly start: number;
  readonly total: number;
}

/**
 * One presence appearance ready to play: a frame per state, the initial and inside states, and its strips
 * keyed by `from * STATE_KEY + to`.
 */
interface PreparedPresence {
  readonly states: readonly ThingFrame[];
  readonly initial: number;
  readonly inside: number;
  readonly strips: ReadonlyMap<number, PreparedStrip>;
}

/**
 * What one prop shows: the state it rests on, or the strip playing with where it stands in it and which way
 * it runs, and the time of its last update.
 */
interface Slot {
  readonly look: PreparedPresence;
  seeded: boolean;
  rest: number;
  strip: PreparedStrip | null;
  position: number;
  direction: number;
  lastMs: number;
}

/**
 * Multiplier that keys a strip by its pair of state indices; above the 64 states an appearance may have.
 */
const STATE_KEY = 64;

/**
 * A tile reference with its frame id.
 *
 * @param ref The tile reference.
 * @returns The frame.
 */
function presenceFrame(ref: TileRef): ThingFrame {
  return { ref, id: frameId(ref.tileset, null, ref.tile) };
}

/**
 * Precomputes a presence appearance's frames and strips.
 *
 * @param look The appearance.
 * @param inside Its inside state's index.
 * @returns The prepared appearance.
 */
function prepare(look: ManifestObjectAppearance, inside: number): PreparedPresence {
  const strips = new Map<number, PreparedStrip>();
  for (const transition of look.transitions ?? []) {
    const ends: number[] = [];
    let total = 0;
    for (const duration of transition.durations) {
      total += duration;
      ends.push(total);
    }
    strips.set(transition.from * STATE_KEY + transition.to, { from: transition.from, to: transition.to, frames: transition.frames.map(presenceFrame), ends, start: ends[0] ?? 0, total });
  }
  return { states: look.states.map((state) => presenceFrame(state.frame)), initial: look.initial, inside, strips };
}

/**
 * The presence props of a client's world.
 */
export class PresencePlayback {
  private readonly prepared: readonly (PreparedPresence | null)[];
  private readonly slots: Slot[] = [];

  /**
   * How many strips have started playing, for the page's diagnostics.
   */
  public started = 0;

  /**
   * @param appearances The world version's appearance table.
   */
  public constructor(private readonly appearances: readonly ManifestObjectAppearance[]) {
    this.prepared = appearances.map((look) => (look.inside === undefined ? null : prepare(look, look.inside)));
  }

  /**
   * How many props have been added.
   *
   * @returns The count.
   */
  public get size(): number {
    return this.slots.length;
  }

  /**
   * Adds a prop, resting on its appearance's initial state until its first update.
   *
   * @param appearance The prop's appearance index.
   * @returns The prop's slot.
   * @throws RangeError when the table has no such appearance or the appearance names no inside state.
   */
  public add(appearance: number): number {
    if (this.appearances[appearance] === undefined) {
      throw new RangeError(`appearance ${appearance} is not in the table`);
    }
    const look = this.prepared[appearance];
    if (look === null || look === undefined) {
      throw new RangeError(`appearance ${appearance} names no inside state`);
    }
    this.slots.push({ look, seeded: false, rest: look.initial, strip: null, position: 0, direction: 1, lastMs: 0 });
    return this.slots.length - 1;
  }

  /**
   * Advances a prop to now, the viewer's character inside its area or not. The strip playing moves by the
   * time since the last update in the direction it was running, then turns towards the state the character
   * now asks for; a prop at rest in the other state starts its strip.
   *
   * @param slot The prop's slot.
   * @param inside Whether the viewer's own character stands in the prop's area.
   * @param nowMs The client clock in milliseconds.
   * @throws RangeError when the slot was never added.
   */
  public update(slot: number, inside: boolean, nowMs: number): void {
    const held = this.slotAt(slot);
    const target = inside ? held.look.inside : held.look.initial;
    if (!held.seeded) {
      held.seeded = true;
      held.rest = target;
      held.strip = null;
      held.lastMs = nowMs;
      return;
    }
    const elapsed = Math.max(0, nowMs - held.lastMs);
    held.lastMs = nowMs;
    const strip = held.strip;
    if (strip !== null) {
      held.position += held.direction * elapsed;
      if (held.position >= strip.total) {
        held.rest = strip.to;
        held.strip = null;
      } else if (held.position < strip.start) {
        held.rest = strip.from;
        held.strip = null;
      } else {
        held.direction = target === strip.to ? 1 : -1;
        return;
      }
    }
    if (held.rest !== target) {
      this.start(held, target);
    }
  }

  /**
   * The frame a prop shows now: the frame of the strip playing where it stands, or the state it rests on.
   *
   * @param slot The prop's slot.
   * @returns The frame, computed once when the world loaded.
   * @throws RangeError when the slot was never added.
   */
  public frame(slot: number): ThingFrame {
    const held = this.slotAt(slot);
    const strip = held.strip;
    if (strip === null) {
      return held.look.states[held.rest] as ThingFrame;
    }
    for (let i = 1; i < strip.ends.length; i += 1) {
      if (held.position < (strip.ends[i] as number)) {
        return strip.frames[i] as ThingFrame;
      }
    }
    return strip.frames[strip.frames.length - 1] as ThingFrame;
  }

  /**
   * Makes every prop's next update first sight, as a welcome does: a new session's position owes nothing to
   * the last one.
   */
  public reset(): void {
    for (const held of this.slots) {
      held.seeded = false;
      held.strip = null;
    }
  }

  /**
   * Starts the strip from a prop's resting state to another, at its second frame, or cuts when the author
   * named none or it has a single frame.
   *
   * @param held The prop's record.
   * @param target The state to move to.
   */
  private start(held: Slot, target: number): void {
    const strip = held.look.strips.get(held.rest * STATE_KEY + target);
    if (strip === undefined || strip.frames.length < 2) {
      held.rest = target;
      return;
    }
    held.strip = strip;
    held.position = strip.start;
    held.direction = 1;
    this.started += 1;
  }

  /**
   * A slot's record.
   *
   * @param slot The slot.
   * @returns The record.
   * @throws RangeError when the slot was never added.
   */
  private slotAt(slot: number): Slot {
    const held = this.slots[slot];
    if (held === undefined) {
      throw new RangeError(`presence slot ${slot} was never added`);
    }
    return held;
  }
}
